import {
  type QueryOptions,
  useQuery,
  type UseQueryResult,
} from '@tanstack/react-query';
import { z } from 'zod';
import { type ApiError } from '../../../../utils/api/ApiError/ApiError';
import { fetcher } from '../../../../utils/api/fetcher/fetcher';

// Define the zod schema for PCMAssetDto
export const assetShape = z
  .object({
    pcmId: z.string(),
    assetType: z.string(),
    assetUriReference: z.string(),
    assetTypeLabel: z.string().optional(),
    contentType: z.string(),
    contentTypeLabel: z.string().optional(),
    contentTypeCategory: z.string().optional(),
    titleDisplay: z.string().optional(),
    titleSearchable: z.string().optional(),
    descriptionDisplay: z.string().optional(),
    descriptionSearchable: z.string().optional(),
    language: z.string().optional(),
    lexile: z.string().optional(),
    skills: z.array(z.string()).optional(),
    topics: z.array(z.string()).optional(),
    standards: z.array(z.string()).optional(),
  })
  .passthrough(); // Allow additional properties

export type Asset = z.infer<typeof assetShape>;

export type GetAssetApiParams = {
  assetId: string;
};

async function getAssetApi({ assetId }: GetAssetApiParams) {
  const asset = await fetcher(`/assets/${assetId}`, {
    service: '/composite',
    version: '/v1',
  });

  return assetShape.parse(asset);
}

export const getAssetApiQueryKey = (args?: GetAssetApiParams) => {
  const { assetId } = args ?? {};

  return ['/v1', 'assets', assetId].filter(Boolean) as string[];
};

export function useGetAssetApi(
  args: GetAssetApiParams,
  customQueryOptions?: QueryOptions<Asset, ApiError>
): UseQueryResult<Asset, ApiError> {
  return useQuery({
    queryKey: getAssetApiQueryKey(args),
    queryFn: () => getAssetApi(args),
    enabled: !!args.assetId,
    staleTime: 90000, // Match the original staleTime
    ...customQueryOptions,
  });
}
